import BaseScene from "./BaseScene"
import * as THREE from "three"
import { buildPositionMatrix } from "../../../lib/hexGrid"
import blogClient from "../../clients/blog"
import {
  setTorusDimensions,
  getHexagonGeometry,
  meshForGeometryAndImage,
} from "./utils"

const TWO_PI = 2 * Math.PI
const R = 20
const r = 4
const CAMERA_SPEED_FRICTION = 0.05
const PHI_THROTTLE_DELTA = 0.01
const THETA_THROTTLE_DELTA = 0.005

// keep in sync with feedUtils/constants.js
const THETA_POSITIONS = 32
const THETA_DIVISIONS = 3 * THETA_POSITIONS
const PHI_POSITIONS = 26 // even

const THETA_INTERVAL = TWO_PI / THETA_DIVISIONS
const PHI_INTERVAL = TWO_PI / PHI_POSITIONS

// One clean, non-overlapping layer. A regular cell tiles at
// thetaSize = THETA_INTERVAL and phiSize = PHI_INTERVAL / sin(60deg); the 0.94
// pulls each hexagon in a touch so a thin gap shows between every tile.
const HEX_THETA_SIZE = 0.94 * THETA_INTERVAL
const HEX_PHI_SIZE = (0.94 * PHI_INTERVAL) / Math.sin(Math.PI / 3)

setTorusDimensions(R, r)

class Torus extends BaseScene {
  constructor() {
    super()

    this.thetaGroup = new THREE.Group()
    this.group = this.thetaGroup
    this.phiGroup = new THREE.Group()

    this.cameraPosZ = 0

    this.positionTheta = 4.28
    this.positionPhi = 34.7

    this.speedTheta = 0.005
    this.speedPhi = 0

    this.keyHandler = {
      ArrowRight: () => (this.speedTheta -= THETA_THROTTLE_DELTA),
      ArrowLeft: () => (this.speedTheta += THETA_THROTTLE_DELTA),
      ArrowUp: () => (this.speedPhi -= PHI_THROTTLE_DELTA),
      ArrowDown: () => (this.speedPhi += PHI_THROTTLE_DELTA),
      Space: () => console.log(this.positionTheta, this.positionPhi),
    }
  }

  swipeHandler = event => {
    this.speedTheta += 0.01 * event.overallVelocityX
    this.speedPhi -= 0.01 * event.overallVelocityY
  }

  handlePosts = items => {
    items.forEach((item, index) => {
      const mesh = meshForGeometryAndImage(
        getHexagonGeometry(
          item.position.x,
          item.position.y,
          HEX_THETA_SIZE,
          HEX_PHI_SIZE
        ),
        item.image,
        this.renderer
      )

      mesh.link = item.link
      this.thetaGroup.add(mesh)
    })
  }

  // Outline every hexagon cell that has no post, with a hairline white edge, so
  // the empty part of the grid reads as a honeycomb instead of black void.
  drawEmptyCells = items => {
    const key = (x, y) => `${x.toFixed(3)},${y.toFixed(3)}`
    const occupied = new Set(
      items.map(item => key(item.position.x, item.position.y))
    )

    // one merged geometry -> one draw call for every empty-cell outline
    const outlineGeom = new THREE.Geometry()
    this.hexagonCoordMatrix.forEach(rowArray => {
      rowArray.forEach(coords => {
        if (occupied.has(key(coords.x, coords.y))) return
        const { vertices } = getHexagonGeometry(
          coords.x,
          coords.y,
          HEX_THETA_SIZE,
          HEX_PHI_SIZE
        )
        for (let i = 1; i <= 6; i++) {
          outlineGeom.vertices.push(vertices[i], vertices[i === 6 ? 1 : i + 1])
        }
      })
    })

    const outlines = new THREE.LineSegments(
      outlineGeom,
      new THREE.LineBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.35,
      })
    )
    outlines.raycast = () => {} // taps pass through to the posts behind
    this.thetaGroup.add(outlines)
  }

  setupTorus = () => {
    this.hexagonCoordMatrix = buildPositionMatrix(
      THETA_POSITIONS,
      PHI_POSITIONS
    )

    this.thetaGroup.position.z = -0.95 * R

    this.phiGroup.add(this.thetaGroup)

    this.scene.add(this.phiGroup)

    blogClient.Post.list().then(response => {
      const layout = response.data().layout
      console.log({ layout })
      if (layout.initialTheta) this.positionTheta = layout.initialTheta
      if (layout.initialPhi) this.positionPhi = layout.initialPhi

      const items = response.data().items
      this.handlePosts(items)
      this.drawEmptyCells(items)
    })

    this.animate()
  }

  handleObjectTapped = ({ link }) => {
    document.location.href = link
  }

  updateCameraPosition = () => {
    this.speedTheta *= 1.0 - CAMERA_SPEED_FRICTION
    this.speedPhi *= 1.0 - CAMERA_SPEED_FRICTION

    this.positionPhi += this.speedPhi
    this.positionTheta += this.speedTheta

    this.thetaGroup.rotation.y = this.positionTheta
    this.phiGroup.rotation.x = this.positionPhi
  }

  componentDidMount() {
    super.componentDidMount()

    this.camera.position.set(this.cameraPosX, this.cameraPosY, this.cameraPosZ)

    this.setupTorus()
  }
}

export default Torus
